import React, { useState, useEffect } from 'react';
import { message, Button, Drawer } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import OrgListTable from '../../components/orgManagement/OrgListTable';
import AddOrgModal from '../../components/orgManagement/AddOrgModal';
import AddUserToOrgModal from '../../components/orgManagement/AddUserToOrgModal';
import OrgWabaTable from '../../components/orgManagement/OrgWabaTable';
import OrgUsersTable from '../../components/orgManagement/OrgUsersTable';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { OrganizationSummary } from '../../interfaces/orgManagement';
import CommonLayout from '../../common/LayoutBlock';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useMetaSetup } from '../../context/MetaSetupContext';

const OrgManagement: React.FC = () => {
    const { currentOrg, refreshSession } = useAuthOrg();
    const { orgWabaData, fetchWabaDetails, loadingWaba } = useMetaSetup();
    const { setBreadcrumbs } = useBreadcrumbs();

    // Managed Orgs State
    const [managedOrgs, setManagedOrgs] = useState<OrganizationSummary[]>([]);
    const [addOrgModalVisible, setAddOrgModalVisible] = useState(false);

    // Add User State
    const [addUserModalVisible, setAddUserModalVisible] = useState(false);
    const [targetOrg, setTargetOrg] = useState<OrganizationSummary | null>(null);

    // Drawer State
    const [wabaDrawerVisible, setWabaDrawerVisible] = useState(false);
    const [usersDrawerVisible, setUsersDrawerVisible] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<OrganizationSummary | null>(null);

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg">Settings</div>,
            },
            {
                title: <div className="text-lg">Account Management</div>,
            },
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (orgWabaData) {
            const mappedOrgs: OrganizationSummary[] = orgWabaData.map(org => ({
                orgId: org.orgId,
                orgName: org.orgName,
                status: 'active', // Defaulting to active as context doesn't have status
                createdAt: new Date().toISOString() // Placeholder
            }));
            setManagedOrgs(mappedOrgs);
        }
    }, [orgWabaData]);

    const handleAddOrgSuccess = () => {
        fetchWabaDetails();
        setAddOrgModalVisible(false);
        refreshSession();
        message.success('Account added. Please refresh to see changes.');
    };

    const handleAddUserClick = (org: OrganizationSummary) => {
        setTargetOrg(org);
        setAddUserModalVisible(true);
    };

    const handleAddUserSuccess = () => {
        setAddUserModalVisible(false);
        setTargetOrg(null);
    };

    const handleViewWaba = (org: OrganizationSummary) => {
        setSelectedOrg(org);
        setWabaDrawerVisible(true);
    };

    const handleViewUsers = (org: OrganizationSummary) => {
        setSelectedOrg(org);
        setUsersDrawerVisible(true);
    };

    const closeDrawers = () => {
        setWabaDrawerVisible(false);
        setUsersDrawerVisible(false);
        setSelectedOrg(null);
    };

    if (!currentOrg) return null;

    return (
        <CommonLayout>
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">Account Management</h1>
                    <p className="text-gray-500">Manage your sub-accounts and their users</p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    className="bg-[#1F344C]"
                    onClick={() => setAddOrgModalVisible(true)}
                >
                    Add Account
                </Button>
            </div>

            <div className='bg-gray-50 p-4'>
                <OrgListTable
                    orgs={managedOrgs}
                    loading={false}
                    onAddUser={handleAddUserClick}
                    onViewWaba={handleViewWaba}
                    onViewUsers={handleViewUsers}
                />
            </div>

            <AddOrgModal
                visible={addOrgModalVisible}
                onCancel={() => setAddOrgModalVisible(false)}
                onSuccess={handleAddOrgSuccess}
            />

            {targetOrg && (
                <AddUserToOrgModal
                    visible={addUserModalVisible}
                    onCancel={() => {
                        setAddUserModalVisible(false);
                        setTargetOrg(null);
                    }}
                    onSuccess={handleAddUserSuccess}
                    orgId={targetOrg.orgId}
                    orgName={targetOrg.orgName}
                />
            )}

            <Drawer
                title={selectedOrg ? `WABA Accounts - ${selectedOrg.orgName}` : 'WABA Accounts'}
                placement="right"
                onClose={closeDrawers}
                open={wabaDrawerVisible}
                width={600}
            >
                {selectedOrg && <OrgWabaTable orgId={selectedOrg.orgId} />}
            </Drawer>

            <Drawer
                title={selectedOrg ? `Assigned Users - ${selectedOrg.orgName}` : 'Assigned Users'}
                placement="right"
                onClose={closeDrawers}
                open={usersDrawerVisible}
                width={600}
            >
                {selectedOrg && <OrgUsersTable orgId={selectedOrg.orgId} />}
            </Drawer>
        </CommonLayout>
    );
};

export default OrgManagement;
